import { useState, type FormEvent } from "react";
import { wordBankApi } from "../../api/endpoints.js";
import type { TipoPrueba } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Field, TextInput, Select, ErrorBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";

export function CustomTestsPage() {
  const t = useT();
  const pruebas = useQuery(() => wordBankApi.pruebas());
  const palabras = useQuery(() => wordBankApi.palabras());

  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tipo, setTipo] = useState<TipoPrueba>("fonologica");
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [borrandoId, setBorrandoId] = useState<string | null>(null);

  function toggle(id: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onCrear(ev: FormEvent) {
    ev.preventDefault();
    setFormError(null);
    if (!nombre.trim() || seleccion.size === 0) {
      setFormError("Indica un nombre y selecciona al menos una palabra.");
      return;
    }
    setEnviando(true);
    try {
      const desc = descripcion.trim();
      await wordBankApi.crearPrueba({
        nombre: nombre.trim(),
        tipo,
        palabraIds: Array.from(seleccion),
        ...(desc ? { descripcion: desc } : {}),
      });
      setNombre("");
      setDescripcion("");
      setTipo("fonologica");
      setSeleccion(new Set());
      pruebas.reload();
    } catch (e) {
      setFormError(mensajeDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  async function onEliminar(id: string) {
    setBorrandoId(id);
    try {
      await wordBankApi.eliminarPrueba(id);
      pruebas.reload();
    } catch {
      /* best-effort; el usuario puede reintentar */
    } finally {
      setBorrandoId(null);
    }
  }

  return (
    <>
      <ScreenHeader title={t("therapist.tests.title")} back="/terapeuta" />
      <Content>
        <Card>
          <h2>{t("therapist.tests.title")}</h2>
          {pruebas.loading ? (
            <Loader label={t("common.loading")} />
          ) : pruebas.error ? (
            <>
              <ErrorBanner message={pruebas.error} />
              <Button variant="ghost" onClick={pruebas.reload}>
                {t("common.retry")}
              </Button>
            </>
          ) : pruebas.data && pruebas.data.length > 0 ? (
            <ul style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {pruebas.data.map((p) => (
                <li
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--radius)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.nombre}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>
                      {p.tipo} · {p.propiedad}
                    </div>
                  </div>
                  {p.propiedad === "personalizada" ? (
                    <Button
                      variant="coral"
                      disabled={borrandoId === p.id}
                      onClick={() => void onEliminar(p.id)}
                    >
                      Eliminar
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p>Todavía no hay pruebas. Crea la primera abajo.</p>
          )}
        </Card>

        <Card>
          <h2>{t("therapist.tests.new")}</h2>
          <form onSubmit={onCrear} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
            {formError ? <ErrorBanner message={formError} /> : null}
            <Field label="Nombre">
              <TextInput required value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </Field>
            <Field label="Descripción" hint="Opcional">
              <TextInput value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </Field>
            <Field label="Tipo">
              <Select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPrueba)}>
                <option value="fonologica">Fonológica</option>
                <option value="simplificada">Simplificada</option>
              </Select>
            </Field>

            <Field label={`Palabras (${seleccion.size} seleccionadas)`}>
              {palabras.loading ? (
                <Loader label={t("common.loading")} />
              ) : palabras.error ? (
                <ErrorBanner message={palabras.error} />
              ) : (
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    border: "1px solid var(--line)",
                    borderRadius: "var(--radius)",
                    padding: 10,
                  }}
                >
                  {(palabras.data ?? []).map((pal) => (
                    <label
                      key={pal.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={seleccion.has(pal.id)}
                        onChange={() => toggle(pal.id)}
                      />
                      <span style={{ fontWeight: 600 }}>{pal.texto}</span>
                      <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>
                        {pal.fonemaObjetivo ?? "—"} · {pal.posicion}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </Field>

            <Button
              type="submit"
              block
              disabled={enviando || !nombre.trim() || seleccion.size === 0}
            >
              {enviando ? t("common.saving") : t("common.save")}
            </Button>
          </form>
        </Card>
      </Content>
    </>
  );
}
